import { m as motion } from 'framer-motion';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useRef } from 'react';
import type { Thing } from 'schema-dts';
import { PAGES } from '@/consts';
import { getCommonPageProps } from '@/shared/api';
import { CarsListProps } from '@/shared/api/car-classes';
import { getCarClasses } from '@/shared/api/car-classes';
import { getEmployees } from '@/shared/api/employees';
import { getPageDetail } from '@/shared/api/page-detail';
import { getSiteSettings } from '@/shared/api/site-settings';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import AdditionalInfoSection from '@/shared/components/sections/AdditionalInfoSection';
import { AdditionalInfo } from '@/shared/components/sections/AdditionalInfoSection/AdditionalInfoSection';
import ServiceHeaderSection from '@/shared/components/sections/ServiceHeaderSection';
import ServicesCarsList from '@/shared/components/sections/ServicesCarsListSection';
import ServicesContent from '@/shared/components/sections/ServicesContent';
import { ServicesContentProps } from '@/shared/components/sections/ServicesContent/ServicesContent';
import ServicesContentHeader from '@/shared/components/sections/ServicesContentHeader';
import { ServicesContentHeaderProps } from '@/shared/components/sections/ServicesContentHeader/ServicesContentHeader';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import { AppNavigation, CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { generateWebPageJsonLd } from '@/shared/lib/utils/page-json-ld';

const ServicesPage = ({
    h1,
    pageTitle,
    titleDescription,
    bodyClass,
    breadcrumbs,
    meta,
    navigation,
    servicesContentHeaderProps,
    servicesContentProps,
    additionalInfoProps,
    carsListProps,
    buttonText,
    buttonLink,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const ref = useRef(null);

    return (
        <DefaultLayout currentPageId={PAGES.CAR_RENTAL} bodyClass={bodyClass} ref={ref}>
            <h1 className="visually-hidden">{h1}</h1>
            <ServiceHeaderSection
                serviceHeaderSectionProps={{
                    breadcrumbs,
                    h1: pageTitle,
                    titleDescription,
                    navigation,
                    buttonText,
                    buttonLink,
                }}
            />
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
                className="services-wrapper"
            >
                <ServicesContentHeader servicesContentHeader={servicesContentHeaderProps} />
                <ServicesCarsList carsList={carsListProps} />

                <ServicesContent servicesContent={servicesContentProps} />
                <AdditionalInfoSection variant="medium" additionalInfoSectionProps={additionalInfoProps} />
            </motion.div>
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-car-rent"
                data={generateWebPageJsonLd({
                    name: pageTitle || h1,
                    description: titleDescription || meta.description,
                    pagePath: '/services/car-rent/',
                    breadcrumbs,
                })}
            />
        </DefaultLayout>
    );
};

export default ServicesPage;

type ServicesPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        pageTitle: string;
        navigation: AppNavigation;
        titleDescription: string;
        buttonText: string;
        buttonLink: string;
        servicesContentHeaderProps: ServicesContentHeaderProps;
        carsListProps: CarsListProps;
        servicesContentProps: ServicesContentProps;
        additionalInfoProps: AdditionalInfo;
    }
>;

export const getStaticProps: GetStaticProps<ServicesPageProps> = async () => {
    const pageResponse = await getPageDetail({ params: { slug: 'car-rent' } });

    const [commonPageProps, carClasses, siteSettings, employeesResponse] = await Promise.all([
        getCommonPageProps(pageResponse.data?.id ?? PAGES.CAR_RENTAL),
        getCarClasses(),
        getSiteSettings({
            params: {
                keys: [
                    'car_first_screen_title',
                    'car_first_screen_desription',
                    'car_first_screen_link',
                    'car_first_screen_link_text',

                    'car_cars_title',
                    'car_cars_desription',
                    'car_cars_link',
                    'car_cars_link_text',

                    'car_conditions_title',
                    'car_conditions_desription',

                    'car_contact_title',
                    'car_contact_desription',
                ],
            },
        }),
        getEmployees({ params: { page: pageResponse.data?.id ?? PAGES.CAR_RENTAL } }),
    ]);

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'services-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Сервисы', href: '/services/' },
            ],
            h1: pageResponse.data?.meta?.title || 'Сервисы',
            pageTitle: siteSettings.data?.car_first_screen_title || 'Сервисы',
            titleDescription:
                siteSettings.data?.car_first_screen_desription ||
                'Используйте возможности сервисов, чтобы сделать ваше участие в событиях TNF максимально продуктивным',
            buttonText: siteSettings.data?.car_first_screen_link_text || 'Служба поддержки',
            buttonLink: siteSettings.data?.car_first_screen_link || '',
            navigation: [
                {
                    id: 0,
                    title: 'Бронирование гостиниц',
                    href: '/services/hotels/',
                },
                {
                    id: 1,
                    title: 'Аренда транспорта',
                    href: '/services/car-rent/',
                },
                {
                    id: 2,
                    title: 'Аккредитация транспорта',
                    href: '/services/car-accreditation/',
                },
                {
                    id: 3,
                    title: 'Шаттлы',
                    href: '/services/shuttles/',
                },
                {
                    id: 4,
                    title: 'Зоны питания',
                    href: '/services/food-areas/',
                },
            ],
            servicesContentHeaderProps: {
                title: siteSettings.data?.car_cars_title || 'Класс автомобиля',
                description:
                    siteSettings.data?.car_cars_desription ||
                    'Участники могут арендовать аккредитованный или неаккредитованный (без права доступа на территорию Технопарка) автомобиль.',
                link: {
                    href: siteSettings.data?.car_cars_link || 'https://forms.yandex.ru/cloud/6a5f1f3449af47cafe247660/',
                    name: siteSettings.data?.car_cars_link_text || 'Отправить запрос',
                },
            },
            carsListProps: carClasses.data || [],

            servicesContentProps: {
                title: siteSettings.data?.car_conditions_title || 'Условия аренды',
                content: siteSettings.data?.car_conditions_desription || '',
            },

            additionalInfoProps: {
                title: siteSettings.data?.car_contact_title || 'Дополнительная информация',
                subtitle:
                    siteSettings.data?.car_contact_desription ||
                    'Заказать услугу и получить дополнительную информацию об условиях можно у оператора Форума в сфере гостеприимства – компании Promo Travel',
                contacts: employeesResponse.data || [],
            },
        } satisfies ServicesPageProps,
        revalidate: 60,
    };
};
